import { useBalance, usePayments } from '@/hooks/api-hooks';
import { Business, ClientBalanceEntry, Person } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from '@mantine/core';
import { IconPrinter, IconReceipt } from '@tabler/icons-react';
import BalanceDetails from './balance-details';
import SendBalanceInput from './send-balance-input';
import { useMemo } from 'react';

interface Props {
    business?: Business;
    person?: Person;
}

export default function BalanceTab({ business, person }: Props): JSX.Element {
    const { t } = useTranslation();

    const targetId = business?.id || person?.id;
    const clientType = business ? 'business' : 'person';

    const {
        data: balanceRes,
        isLoading: isLoadingBalance
    } = useBalance({
        find_by_id: targetId,
        find_by_id_type: clientType,
    });

    const {
        data: paymentsRes,
    } = usePayments(
        business?.id ? { business_id: business.id } : {},
        { enabled: !!business?.id }
    );

    const matchingBalances = useMemo(() => {
        if (!targetId || !balanceRes?.data) return [];

        return balanceRes.data.map(group => {
            const foundClient = group.clients.find(c => c.id === targetId);
            if (!foundClient) return null;

            return {
                ownedBusinessName: group.name,
                ownedBusinessId: group.id,
                client: foundClient,
            };
        }).filter((x): x is {
            ownedBusinessName: string;
            ownedBusinessId: number;
            client: ClientBalanceEntry;
        } => !!x);
    }, [targetId, balanceRes]);

    const hasBalances = matchingBalances.length > 0;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2.5 mb-2 border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-650 shrink-0">
                        <IconReceipt size={18} stroke={2} />
                    </div>
                    <div className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                        {t('account_statement')}
                    </div>
                    {isLoadingBalance && <Loader size="xs" color="black" />}
                </div>
                <SendBalanceInput
                    business={business}
                />
            </div>

            {/* Account Statement Section */}
            <div className="space-y-4">
                {matchingBalances.map(({ ownedBusinessName, ownedBusinessId, client }) => {
                    const clientName = business?.name || person?.name || '--';
                    return (
                        <div key={ownedBusinessId} className="space-y-1">
                            <div className="text-xs font-bold text-slate-800 flex flex-wrap items-center gap-1">
                                <div className="text-slate-500 font-medium">{t('client')}:</div>
                                <div className="bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md text-gray-900 font-bold">{clientName}</div>
                                <div className="text-slate-500 font-medium">{t('seller')}:</div>
                                <div className="text-slate-800 font-light">{ownedBusinessName}</div>
                            </div>
                            <BalanceDetails selectedClient={client} payments={paymentsRes?.data} />
                        </div>
                    );
                })}

                {!hasBalances && !isLoadingBalance && (
                    <div className='text-sm font-light text-gray-400 italic py-4 text-center bg-slate-50 border border-slate-200/60 rounded-xl'>
                        {t('nothing_consumed_or_paid_yet')}
                    </div>
                )}
            </div>
        </div>
    );
}
